/**
 * Forgot password — public page where users request a reset email.
 * Always shows the same success message to avoid leaking which emails
 * exist on the platform.
 */
import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Mail, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "../lib/api";

export default function ForgotPassword() {
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);
    const [sent, setSent] = useState(false);
    const navigate = useNavigate();

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await api.post("/auth/forgot-password", { email: email.trim() });
            setSent(true);
        } catch (e2) {
            toast.error(apiError(e2));
        } finally { setLoading(false); }
    };

    return (
        <div className="min-h-screen bg-background flex items-center justify-center px-4">
            <div className="max-w-md w-full" data-testid="forgot-password-page">
                <Link to="/login" data-testid="forgot-back-to-login"
                      className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-8">
                    <ArrowLeft className="w-3 h-3" strokeWidth={1.5}/> Back to sign-in
                </Link>
                <div className="bg-card border border-border p-6">
                    <div className="flex items-center gap-2 mb-4">
                        <Mail className="w-4 h-4 text-primary" strokeWidth={1.5}/>
                        <h1 className="text-base text-foreground">Forgot your password?</h1>
                    </div>
                    {sent ? (
                        <div className="space-y-3" data-testid="forgot-sent-state">
                            <p className="text-xs text-muted-foreground">
                                If an account exists for <span className="text-foreground font-mono">{email}</span>, a
                                reset link has been emailed to it. The link is valid for 24 hours.
                            </p>
                            <p className="text-xs text-muted-foreground">
                                Check your spam folder if you don&apos;t see it. Still no luck? Contact your firm admin.
                            </p>
                            <button onClick={() => navigate("/login")} data-testid="forgot-go-login"
                                className="mt-3 w-full bg-primary text-primary-foreground hover:bg-primary py-2 text-xs font-medium uppercase tracking-[0.16em]">
                                Back to sign-in
                            </button>
                        </div>
                    ) : (
                        <form onSubmit={submit} className="space-y-4">
                            <p className="text-xs text-muted-foreground">
                                Enter your account email. We&apos;ll send a one-time link valid for 24 hours.
                            </p>
                            <div>
                                <label className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground mb-1.5 block">Email</label>
                                <input type="email" value={email} onChange={e => setEmail(e.target.value)} required autoFocus
                                       data-testid="forgot-email-input"
                                       className="w-full bg-background border border-border focus:border-primary outline-none px-3 py-2 text-sm"
                                       placeholder="you@yourfirm.com"/>
                            </div>
                            <button type="submit" disabled={loading || !email} data-testid="forgot-submit"
                                className="w-full bg-primary text-primary-foreground hover:bg-primary disabled:opacity-40 py-2 text-xs font-medium uppercase tracking-[0.16em]">
                                {loading ? "Sending…" : "Send reset link"}
                            </button>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
}
